import React from 'react';
import { Box, Container, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
  createPageContainer, 
  createScrollableContent, 
  createFullHeightLayout,
  createMainContent,
  createPageContent,
  createSidebarLayout,
  createSidebar,
  createMainArea,
  createFixedHeader,
  createFixedFooter,
  createResponsivePageWrapper,
  createContentWrapper,
  createScrollableArea,
  createScrollableContentArea,
  createSidebarScrollableLayout,
  createDialogHeaderStyles,
  createDialogContentAreaStyles,
  createDialogActionsStyles,
  createInfoSectionStyles,
  createTipStyles,
  createDialogLayout,
  createDialogStyles
} from '../utils';
import { alpha } from '@mui/material/styles';
import { useRef, useState } from 'react';

// Main page container component
export const PageContainer = ({ children, className = '', ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`page-container ${className}`}
      sx={createPageContainer(theme, props)}
      {...props}
    >
      {children}
    </Box>
  );
};

// Scrollable content area component
export const ScrollableContent = ({ children, className = '', padding, ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`scrollable-content ${className}`}
      sx={createScrollableContent(theme, { padding, ...props })}
      {...props}
    >
      {children}
    </Box>
  );
};

// Full height layout component
export const FullHeightLayout = ({ children, className = '', ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`full-height-layout ${className}`}
      sx={createFullHeightLayout(theme, props)}
      {...props}
    >
      {children}
    </Box>
  );
};

// Main content area component
export const MainContent = ({ children, className = '', ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`main-content ${className}`}
      sx={createMainContent(theme, props)}
      {...props}
    >
      {children}
    </Box>
  );
};

// Page content with responsive padding - matches header width constraints
export const PageContent = ({ 
  children, 
  className = '', 
  responsive = true, 
  padding, 
  constrained = true, 
  scrollable = false, // New prop to control scrolling mode
  ...props 
}) => {
  const theme = useTheme();
  
  const scrollClass = scrollable ? 'scrollable' : '';
  const combinedClassName = `page-content ${scrollClass} ${className}`.trim();
  
  if (constrained) {
    return (
      <Box
        className={combinedClassName}
        sx={createPageContent(theme, { responsive, padding, scrollable, ...props })}
        {...props}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          {children}
        </Container>
      </Box>
    );
  }
  
  return (
    <Box
      className={combinedClassName}
      sx={createPageContent(theme, { responsive, padding, scrollable, ...props })}
      {...props}
    >
      {children}
    </Box>
  );
};

// Sidebar layout component
export const SidebarLayout = ({ children, className = '', ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`sidebar-layout ${className}`}
      sx={createSidebarLayout(theme, props)}
      {...props}
    >
      {children}
    </Box>
  );
};

// Sidebar component
export const Sidebar = ({ children, className = '', width, backgroundColor, ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`sidebar ${className}`}
      sx={createSidebar(theme, { width, backgroundColor, ...props })}
      {...props}
    >
      {children}
    </Box>
  );
};

// Main area (content next to sidebar)
export const MainArea = ({ children, className = '', ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`main-area ${className}`}
      sx={createMainArea(theme, props)}
      {...props}
    >
      {children}
    </Box>
  );
};

// Fixed header component
export const FixedHeader = ({ children, className = '', backgroundColor, ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`fixed-header ${className}`}
      sx={createFixedHeader(theme, { backgroundColor, ...props })}
      {...props}
    >
      {children}
    </Box>
  );
};

// Fixed footer component
export const FixedFooter = ({ children, className = '', backgroundColor, ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`fixed-footer ${className}`}
      sx={createFixedFooter(theme, { backgroundColor, ...props })}
      {...props}
    >
      {children}
    </Box>
  );
};

// Responsive page wrapper
export const ResponsivePageWrapper = ({ children, className = '', ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`responsive-page-wrapper ${className}`}
      sx={createResponsivePageWrapper(theme, props)}
      {...props}
    >
      {children}
    </Box>
  );
};

// Content wrapper with proper scrolling
export const ContentWrapper = ({ children, className = '', ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`content-wrapper ${className}`}
      sx={createContentWrapper(theme, props)}
      {...props}
    >
      {children}
    </Box>
  );
};

// Scrollable area component
export const ScrollableArea = ({ children, className = '', scrollbarType, ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      className={`scrollable-area ${className}`}
      sx={createScrollableArea(theme, { scrollbarType, ...props })}
      {...props}
    >
      {children}
    </Box>
  );
};

// Complete page layout with header, content, and optional footer
export const AppPageLayout = ({ 
  header, 
  children, 
  footer, 
  sidebar,
  className = '',
  headerProps = {},
  contentProps = {},
  footerProps = {},
  sidebarProps = {},
  constrainContent = true, // New prop to control content width constraints
  ...props 
}) => {
  const wrappedChildren = constrainContent ? (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, height: '100%' }}>
      {children}
    </Container>
  ) : children;
  
  if (sidebar) {
    return (
      <SidebarLayout className={className} {...props}>
        <Sidebar {...sidebarProps}>
          {sidebar}
        </Sidebar>
        <MainArea>
          {header && (
            <FixedHeader {...headerProps}>
              {header}
            </FixedHeader>
          )}
          <ScrollableContent {...contentProps}>
            {wrappedChildren}
          </ScrollableContent>
          {footer && (
            <FixedFooter {...footerProps}>
              {footer}
            </FixedFooter>
          )}
        </MainArea>
      </SidebarLayout>
    );
  }
  
  return (
    <FullHeightLayout className={className} {...props}>
      {header && (
        <FixedHeader {...headerProps}>
          {header}
        </FixedHeader>
      )}
      <ScrollableContent {...contentProps}>
        {wrappedChildren}
      </ScrollableContent>
      {footer && (
        <FixedFooter {...footerProps}>
          {footer}
        </FixedFooter>
      )}
    </FullHeightLayout>
  );
};

// Simple wrapper component that constrains content to header width
export const HeaderConstrainedContent = ({ children, className = '', noPadding = false, ...props }) => {
  return (
    <Container 
      maxWidth="xl" 
      className={className}
      sx={{
        px: { xs: 2, sm: 3 },
        py: noPadding ? 0 : { xs: 2, sm: 3 },
        ...props.sx
      }}
      {...props}
    >
      {children}
    </Container>
  );
};

// Container with max width and centering - respects header constraints
export const CenteredPageContent = ({ 
  children, 
  maxWidth = 'xl', // Default to xl to match header
  className = '',
  responsive = true,
  constrained = false, // Don't double-constrain
  ...props 
}) => {
  return (
    <PageContent responsive={responsive} className={className} constrained={constrained} {...props}>
      <Container maxWidth={maxWidth} sx={{ height: '100%', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
        {children}
      </Container>
    </PageContent>
  );
};

// Grid-based page layout - respects header constraints
export const GridPageLayout = ({ 
  children, 
  spacing = 3, 
  className = '',
  responsive = true,
  constrained = true,
  ...props 
}) => {
  return (
    <PageContent responsive={responsive} className={className} constrained={constrained} {...props}>
      <Box
        sx={{
          display: 'grid',
          gap: spacing,
          height: '100%',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(auto-fit, minmax(300px, 1fr))',
            md: 'repeat(auto-fit, minmax(350px, 1fr))',
          }
        }}
      >
        {children}
      </Box>
    </PageContent>
  );
};

// Flex-based page layout - respects header constraints
export const FlexPageLayout = ({ 
  children, 
  direction = 'column', 
  gap = 3,
  className = '',
  responsive = true,
  constrained = true,
  ...props 
}) => {
  return (
    <PageContent responsive={responsive} className={className} constrained={constrained} {...props}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: direction,
          gap: gap,
          height: '100%',
          ...props.sx
        }}
      >
        {children}
      </Box>
    </PageContent>
  );
};

// Split layout (two columns) - respects header constraints
export const SplitPageLayout = ({ 
  left, 
  right, 
  leftWidth = '50%',
  gap = 2,
  className = '',
  responsive = true,
  constrained = true,
  ...props 
}) => {
  return (
    <PageContent responsive={responsive} className={className} constrained={constrained} {...props}>
      <Box
        sx={{
          display: 'flex',
          gap: gap,
          height: '100%',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box sx={{ 
          width: { xs: '100%', md: leftWidth },
          minHeight: 0,
          overflow: 'hidden'
        }}>
          {left}
        </Box>
        <Box sx={{ 
          flex: 1,
          minHeight: 0,
          overflow: 'hidden'
        }}>
          {right}
        </Box>
      </Box>
    </PageContent>
  );
};

// Dashboard layout with multiple sections - respects header constraints
export const DashboardLayout = ({ 
  header,
  sidebar,
  main,
  aside,
  footer,
  className = '',
  constrainContent = true,
  ...props 
}) => {
  const wrappedMain = constrainContent ? (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, height: '100%' }}>
      {main}
    </Container>
  ) : main;

  return (
    <FullHeightLayout className={className} {...props}>
      {header && (
        <FixedHeader>
          {header}
        </FixedHeader>
      )}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {sidebar && (
          <Sidebar width="280px">
            {sidebar}
          </Sidebar>
        )}
        <MainArea>
          <ScrollableContent>
            {wrappedMain}
          </ScrollableContent>
        </MainArea>
        {aside && (
          <Box sx={{ 
            width: '320px', 
            flexShrink: 0,
            overflowY: 'auto',
            borderLeft: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper'
          }}>
            {aside}
          </Box>
        )}
      </Box>
      {footer && (
        <FixedFooter>
          {footer}
        </FixedFooter>
      )}
    </FullHeightLayout>
  );
};

// Scrollable content area component - for main content that needs to scroll
export const ScrollableContentArea = ({ children, height, ...props }) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        ...createScrollableContentArea(theme, { height, ...props }),
      }}
    >
      {children}
    </Box>
  );
};

// Sidebar + Scrollable Main Content Layout - perfect for admin panels, dashboards, etc.
export const SidebarScrollableLayout = ({ 
  sidebar, 
  children, 
  className = '', 
  sidebarWidth = '280px',
  spacing = 3,
  stickyTop = 24,
  constrained = true,
  heightConfig = {}, // Allow custom height configuration
  ...props 
}) => {
  const theme = useTheme();
  
  // Use precise height calculation for bottom-touching
  const layoutConfig = {
    sidebarWidth, 
    spacing, 
    stickyTop,
    // Precise height calculation for bottom-touching
    extraClearance: 0, // No extra clearance for bottom-touching
    hasHeader: true, // Assume header is present
    containerPadding: 0, // Don't account for container padding in height
    ...heightConfig
  };
  
  const layout = createSidebarScrollableLayout(theme, layoutConfig);
  
  const wrappedChildren = constrained ? (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, height: '100%' }}>
      {children}
    </Container>
  ) : children;
  
  return (
    <PageContent scrollable className={className} {...props}>
      <Box sx={layout.container}>
        {/* Sidebar */}
        <Box sx={layout.sidebar}>
          {sidebar}
        </Box>
        
        {/* Main Content */}
        <Box sx={layout.mainContent}>
          {wrappedChildren}
        </Box>
      </Box>
    </PageContent>
  );
};

// Split Dialog Layout Component
export const SplitDialog = ({ 
  open, 
  onClose, 
  title,
  subtitle,
  sidebarContent,
  mainContent,
  actions,
  sidebarProps = {},
  mainContentProps = {},
  ...dialogProps 
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
      {...dialogProps}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        height: { md: '490px' }
      }}>
        {/* Left section with visual background and title */}
        <Box sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          p: 3,
          width: { xs: '100%', md: '240px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          ...sidebarProps
        }}>
          <Box>
            <Typography variant="h5" fontWeight="medium" mb={2}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          
          {sidebarContent}
        </Box>
        
        {/* Right section with content */}
        <Box sx={{ 
          flex: 1, 
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          ...mainContentProps
        }}>
          {mainContent}
          
          {actions && (
            <DialogActions sx={{ p: 0, pt: 2, justifyContent: 'flex-end' }}>
              {actions}
            </DialogActions>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

// Feature Card Component (for dialog content)
export const FeatureCard = ({ 
  icon, 
  title, 
  description, 
  onClick, 
  selected = false,
  ...props 
}) => {
  const theme = useTheme();
  
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{ 
        flex: 1, 
        p: 2, 
        border: '1px solid', 
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        ...(onClick && {
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`
          }
        }),
        ...(selected && {
          borderColor: 'primary.main',
          backgroundColor: alpha(theme.palette.primary.main, 0.05)
        }),
        ...props
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {icon && React.cloneElement(icon, { 
          color: "primary", 
          sx: { mr: 1, ...icon.props?.sx } 
        })}
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );
};

// Upload Zone Component
export const UploadZone = ({ 
  onFileSelect, 
  onDrop,
  accept = "*",
  multiple = false,
  children,
  icon,
  title = "Select Files",
  subtitle,
  ...props 
}) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files && onFileSelect) {
      onFileSelect(files);
    }
    // Clear the input
    event.target.value = null;
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files && onDrop) {
      onDrop(files);
    } else if (files && onFileSelect) {
      onFileSelect(files);
    }
  };

  return (
    <Box
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={isDragOver ? 'drag-over' : ''}
      sx={{ 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
        px: 2,
        borderRadius: 2,
        bgcolor: 'rgba(0, 0, 0, 0.02)',
        border: '2px dashed',
        borderColor: 'divider',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          transform: 'translateY(-1px)',
        },
        '&.drag-over': {
          borderColor: 'primary.main',
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          borderStyle: 'solid',
        },
        ...props
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      
      {children || (
        <>
          {icon && (
            <Box className="upload-icon" sx={{ transition: 'all 0.3s ease' }}>
              {icon}
            </Box>
          )}
          
          <Button
            variant="contained"
            size="large"
            sx={{ 
              px: 4,
              py: 1,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 'bold',
              pointerEvents: 'none' // Prevent double-click
            }}
          >
            {title}
          </Button>
          
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              {subtitle}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

// Dialog Info Section Component (for sidebars)
export const DialogInfoSection = ({ 
  icon, 
  title, 
  subtitle,
  details = [],
  ...props 
}) => {
  const theme = useTheme();
  
  return (
    <Box sx={createInfoSectionStyles(theme, props)}>
      {icon && (
        <Box className="info-icon">
          {icon}
        </Box>
      )}
      
      {title && (
        <Typography variant="body2" className="info-text">
          {title}
        </Typography>
      )}
      
      {subtitle && (
        <Typography variant="body2" className="info-detail">
          {subtitle}
        </Typography>
      )}
      
      {details.map((detail, index) => (
        <Typography key={index} variant="body2" className="info-detail">
          {detail}
        </Typography>
      ))}
    </Box>
  );
};

// Dialog Tip Component
export const DialogTip = ({ 
  label = "Tip:", 
  text, 
  children,
  ...props 
}) => {
  const theme = useTheme();
  
  return (
    <Typography variant="body2" sx={createTipStyles(theme, props)}>
      <Box component="span" className="tip-label">
        {label}
      </Box>
      {text && (
        <Box component="span" className="tip-text">
          {' '}{text}
        </Box>
      )}
      {children}
    </Typography>
  );
};

// Standard Dialog Layout Component
export const StandardDialog = ({ 
  open, 
  onClose, 
  title,
  children,
  actions,
  scrollable = false,
  ...dialogProps 
}) => {
  const theme = useTheme();
  const layout = createDialogLayout(theme, 'standard', dialogProps);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={createDialogStyles(theme, dialogProps)}
      {...dialogProps}
    >
      <Box sx={layout.container}>
        {title && (
          <DialogTitle sx={createDialogHeaderStyles(theme)}>
            {title}
          </DialogTitle>
        )}
        
        <DialogContent sx={createDialogContentAreaStyles(theme, { scrollable })}>
          {children}
        </DialogContent>
        
        {actions && (
          <DialogActions sx={createDialogActionsStyles(theme)}>
            {actions}
          </DialogActions>
        )}
      </Box>
    </Dialog>
  );
};

// Full Width Dialog Layout Component
export const FullWidthDialog = ({ 
  open, 
  onClose, 
  title,
  children,
  actions,
  ...dialogProps 
}) => {
  const theme = useTheme();
  const layout = createDialogLayout(theme, 'fullWidth', dialogProps);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={createDialogStyles(theme, dialogProps)}
      {...dialogProps}
    >
      <Box sx={layout.container}>
        {title && (
          <DialogTitle sx={createDialogHeaderStyles(theme)}>
            {title}
          </DialogTitle>
        )}
        
        <DialogContent sx={createDialogContentAreaStyles(theme, { scrollable: true })}>
          {children}
        </DialogContent>
        
        {actions && (
          <DialogActions sx={createDialogActionsStyles(theme)}>
            {actions}
          </DialogActions>
        )}
      </Box>
    </Dialog>
  );
};

// Wizard Dialog Layout Component
export const WizardDialog = ({ 
  open, 
  onClose, 
  title,
  currentStep,
  totalSteps,
  children,
  onNext,
  onPrevious,
  onFinish,
  nextLabel = "Next",
  previousLabel = "Previous",
  finishLabel = "Finish",
  ...dialogProps 
}) => {
  const theme = useTheme();
  const layout = createDialogLayout(theme, 'wizard', dialogProps);
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      sx={createDialogStyles(theme, dialogProps)}
      {...dialogProps}
    >
      <Box sx={layout.container}>
        {/* Header with stepper */}
        <Box sx={layout.header}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            {title}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {Array.from({ length: totalSteps }, (_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: index <= currentStep 
                    ? theme.palette.primary.main 
                    : theme.palette.divider,
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Step {currentStep + 1} of {totalSteps}
            </Typography>
          </Box>
        </Box>
        
        {/* Content */}
        <Box sx={layout.content}>
          {children}
        </Box>
        
        {/* Actions */}
        <Box sx={layout.actions}>
          <Button
            onClick={onPrevious}
            disabled={isFirstStep}
            variant="outlined"
          >
            {previousLabel}
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
            
            {isLastStep ? (
              <Button onClick={onFinish} variant="contained">
                {finishLabel}
              </Button>
            ) : (
              <Button onClick={onNext} variant="contained">
                {nextLabel}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}; 